﻿import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Form, Input, message } from 'antd';
import { WechatOutlined } from '@ant-design/icons';
import { BIND_TICKET_KEY, mFetch, MApiError, startMpOauth, useMAuth, type MUser } from '../mApi';

// /m 登录页：微信网页授权一键登录 + 账号密码兜底 + 绑定流程
export default function MLogin() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const setAuth = useMAuth((s) => s.setAuth);
  const [loading, setLoading] = useState(false);
  const [oauthErr, setOauthErr] = useState('');
  const [form] = Form.useForm();
  const bindTicket = sessionStorage.getItem(BIND_TICKET_KEY) || '';
  const isBindFlow = params.get('bind') === '1' && !!bindTicket;

  const doLogin = async () => {
    const v = await form.validateFields();
    setLoading(true);
    try {
      const r = await mFetch<{ token: string; user: MUser }>('/admin/auth/login', {
        method: 'POST',
        auth: false,
        body: JSON.stringify({ username: v.username, password: v.password }),
      });
      if (isBindFlow) {
        // 绑定当前微信到该账号
        try {
          const b = await mFetch<{ openidMasked: string }>('/wechat-mp/oauth/bind', {
            method: 'POST',
            headers: { authorization: `Bearer ${r.token}` },
            body: JSON.stringify({ ticket: bindTicket }),
          });
          sessionStorage.removeItem(BIND_TICKET_KEY);
          message.success(`微信已绑定（${b.openidMasked}）`);
        } catch (e) {
          message.warning(e instanceof MApiError ? `登录成功，但绑定失败：${e.message}` : '绑定失败');
        }
      }
      setAuth(r.token, r.user);
      nav('/m', { replace: true });
    } catch (e) {
      message.error(e instanceof MApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  const wechatLogin = async () => {
    setOauthErr('');
    setLoading(true);
    try {
      await startMpOauth();
    } catch (e) {
      setOauthErr(e instanceof MApiError ? e.message : String(e));
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 420, margin: '0 auto', padding: '48px 20px 20px' }}>
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <div style={{ fontSize: 22, fontWeight: 700 }}>DTwince 管理网站</div>
        <div style={{ fontSize: 13, color: '#8a8aa3', marginTop: 6 }}>公众号移动端 · 内容 / 留言 / 看板</div>
      </div>

      {isBindFlow && (
        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          title="首次使用：请登录后台账号完成微信绑定"
          description="绑定后下次可微信一键登录。"
        />
      )}

      {!isBindFlow && (
        <Button
          type="primary"
          block
          size="large"
          icon={<WechatOutlined />}
          loading={loading}
          onClick={wechatLogin}
          data-testid="m-wechat-login"
          style={{ marginBottom: 16 }}
        >
          微信一键登录
        </Button>
      )}

      {oauthErr && (
        <Alert type="warning" showIcon style={{ marginBottom: 16 }} title={oauthErr} description="可先用账号密码登录（绑定后在微信内一键登录）" />
      )}

      <Card size="small" title={isBindFlow ? '登录并绑定' : '账号密码登录'} style={{ background: '#141428', borderColor: '#26264a' }}>
        <Form form={form} layout="vertical" onFinish={doLogin}>
          <Form.Item name="username" label="账号" rules={[{ required: true, message: '请输入账号' }]}>
            <Input placeholder="营销后台账号" data-testid="m-username" autoComplete="off" />
          </Form.Item>
          <Form.Item name="password" label="密码" rules={[{ required: true, message: '请输入密码' }]}>
            <Input.Password placeholder="密码" data-testid="m-password" autoComplete="new-password" />
          </Form.Item>
          <Button type="primary" block htmlType="submit" loading={loading} data-testid="m-login-submit">
            {isBindFlow ? '登录并绑定' : '登录'}
          </Button>
        </Form>
      </Card>

      <div style={{ textAlign: 'center', marginTop: 20 }}>
        <a href="/login" style={{ color: '#8a8aa3', fontSize: 12 }}>前往主站登录 →</a>
      </div>
    </div>
  );
}
